import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Props:
 * - myRequests: Array<{ id, slotId, slotDate, slotStartTime, slotEndTime, otherUserName, status }>
 * - incomingRequests: Array<{ id, slotId, requestedSlotIds, slotDate, slotStartTime, slotEndTime, otherUserName }>
 * - timeSlots: Array<{ id, exactDate, startTime, endTime }>    // tüm slot detayları
 * - onDecide: function(requestId: number, approve: boolean, targetSlotId?: number)
 */
export default function SwapRequestsPanel({
	myRequests,
	incomingRequests,
	timeSlots = [],
	onDecide
}) {
	const [selectedSlots, setSelectedSlots] = useState({});

	// İlk render’da, her request için varsayılanı ilk requestedSlotIds[0] yap
	useEffect(() => {
		const init = {};
		incomingRequests.forEach(r => {
			if (r.requestedSlotIds && r.requestedSlotIds.length > 0) {
				init[r.id] = r.requestedSlotIds[0];
			}
		});
		setSelectedSlots(init);
	}, [incomingRequests]);

	const formatDate = isoDate =>
		new Date(isoDate).toLocaleDateString('tr-TR', {
			year: 'numeric', month: 'long', day: 'numeric', weekday: 'long'
		});
	const formatTime = (start, end) =>
		`${start.substring(0, 5)} – ${end.substring(0, 5)}`;

	const getSlotInfo = id => {
		const s = timeSlots.find(ts => ts.id === id);
		if (!s) return { date: '', time: '' };
		const date = s.exactDate.split('T')[0];
		return {
			date: formatDate(date),
			time: formatTime(s.startTime, s.endTime)
		};
	};

	const getStatusColor = status => {
		switch (status) {
			case 'Onaylandı': return 'success';
			case 'Reddedildi':
			case 'İptal Edildi': return 'danger';
			case 'Kullanıcı Onayı Bekliyor':
			case 'Yönetim Onayı Bekliyor': return 'warning';
			default: return 'secondary';
		}
	};

	return (
		<div className="">
			{/* --- Benim Taleplerim --- */}
			<div className="mb-4">
				<h5>Yaptığım Değişim Talepleri</h5>
				{myRequests.length ? myRequests.map(r => {
					const color = getStatusColor(r.status);
					return (
						<div
							key={r.id}
							className={`
                            d-flex justify-content-between align-items-center
                            p-2 border-bottom border-4 border-${color}
                          `}
						>
							<div>
								{formatDate(r.slotDate)} | {formatTime(r.slotStartTime, r.slotEndTime)}<br />
								<strong>Talep Edilen Kişi:</strong> {r.otherUserName}<br />
								<strong>Durum:</strong>{' '}
								<span className={`badge bg-${color} text-uppercase`}>
									{r.status}
								</span>
							</div>
						</div>
					);
				}) : (
					<p>Henüz talebiniz yok.</p>
				)}
			</div>

			{/* --- Gelen Talepler --- */}
			<div>
				<h5>Tarafıma Yapılan Değişim Talepleri</h5>
				{incomingRequests.length ? incomingRequests.map(r => {
					const { date, time } = getSlotInfo(r.slotId);
					return (
						<div
							key={r.id}
							className="p-3 mb-3 border rounded"
						>
							<div className="mb-2">
								<strong>Tarafıma Verilmek İstenen Görev:</strong><br />
								{date} | {time}<br />
								<strong>Talebi yapan kullanıcı:</strong><br />
								{r.otherUserName}
							</div>

							{r.status !== "Kullanıcı Onayı Bekliyor" ? <>
								<span className={`badge bg-warning text-uppercase`}>
									{r.status}
								</span>
							</> :
								<>
									<div className="mb-2">
										<strong>
											{r.requestedSlotIds.length > 1
												? 'Talep Edilen Görevler (Bir tanesini seçerek onaylayabilir ya da isteği reddedebilirsiniz):'
												: 'Talep Edilen Görev (Onaylayabilir ya da reddedebilirsiniz):'}
										</strong>
										<br />
										{r.requestedSlotIds.map(id => {
											const info = getSlotInfo(id);
											return (
												<div className="form-check" key={id}>
													<input
														className="form-check-input"
														type="radio"
														name={`slot-select-${r.id}`}
														id={`slot-${r.id}-${id}`}
														value={id}
														checked={selectedSlots[r.id] === id}
														onChange={() =>
															setSelectedSlots(prev => ({ ...prev, [r.id]: id }))
														}
													/>
													<label
														className="form-check-label"
														htmlFor={`slot-${r.id}-${id}`}
													>
														{info.date} | {info.time}
													</label>
												</div>
											);
										})}
									</div>

									<div className="d-flex justify-content-end">
										<button
											className="btn btn-sm btn-success me-2"
											onClick={() => onDecide(r.id, true, selectedSlots[r.id])}
										>
											Onayla
										</button>
										<button
											className="btn btn-sm btn-danger"
											onClick={() => onDecide(r.id, false, -1)}
										>
											Reddet
										</button>
									</div>
								</>
							}
						</div>
					);
				}) : (
					<p>Size yapılmış talep yok.</p>
				)}
			</div>
		</div>
	);
}

SwapRequestsPanel.propTypes = {
	myRequests: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			slotId: PropTypes.number,
			slotDate: PropTypes.string,
			slotStartTime: PropTypes.string,
			slotEndTime: PropTypes.string,
			otherUserName: PropTypes.string,
			status: PropTypes.string
		})
	).isRequired,
	incomingRequests: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			slotId: PropTypes.number,
			requestedSlotIds: PropTypes.arrayOf(PropTypes.number).isRequired,
			slotDate: PropTypes.string,
			slotStartTime: PropTypes.string,
			slotEndTime: PropTypes.string,
			otherUserName: PropTypes.string
		})
	).isRequired,
	timeSlots: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			exactDate: PropTypes.string.isRequired,
			startTime: PropTypes.string.isRequired,
			endTime: PropTypes.string.isRequired
		})
	).isRequired,
	onDecide: PropTypes.func.isRequired,  // now receives (requestId, approve, targetSlotId?)
};
